import { diffChars } from 'diff'

// A contiguous run of text attributed to one author
export interface Segment {
  text: string
  author: string
}

// Reconstruct the plain text string from a segments array
export function segmentsToText(segments: Segment[]): string {
  return segments.map((s) => s.text).join('')
}

/**
 * Given the old segments, the new plain text, and the current editor's name,
 * returns a new segments array where:
 *  - characters that haven't changed keep their original author
 *  - characters that are new are attributed to `author`
 *
 * Works by building a per-character author lookup for the old text,
 * running a character-level diff, then grouping the result back into segments.
 */
export function applyEdit(
  oldSegments: Segment[],
  newText: string,
  author: string,
): Segment[] {
  const oldText = segmentsToText(oldSegments)

  // Map each character position in the old text to its author
  const charAuthors: string[] = []
  for (const seg of oldSegments) {
    for (let i = 0; i < seg.text.length; i++) {
      charAuthors.push(seg.author)
    }
  }

  // Diff the old and new text at character level
  const changes = diffChars(oldText, newText)

  // Build a flat list of { char, author } pairs
  const resultChars: Array<{ char: string; author: string }> = []
  let oldPos = 0

  for (const change of changes) {
    if (change.added) {
      for (const char of change.value) {
        resultChars.push({ char, author })
      }
    } else if (change.removed) {
      // Skip removed characters, just advance the old-text cursor
      oldPos += change.value.length
    } else {
      // Unchanged — keep the original author for each character
      for (let i = 0; i < change.value.length; i++) {
        resultChars.push({ char: change.value[i], author: charAuthors[oldPos] ?? author })
        oldPos++
      }
    }
  }

  // Group consecutive characters with the same author into segments
  const result: Segment[] = []
  for (const { char, author: a } of resultChars) {
    const last = result[result.length - 1]
    if (last && last.author === a) {
      last.text += char
    } else {
      result.push({ text: char, author: a })
    }
  }

  return result
}
